import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  return (
    <section id="top" data-testid="hero" className="relative min-h-screen w-full overflow-hidden">
      {/* Full-screen background image */}
      <div
        className="absolute inset-0 z-0 bg-[#050505] bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url(/hero-bg.png)" }}
      />

      {/* Dark overlay for legibility — heavier on the left (text), lighter over the shield (right) */}
      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{ background: "linear-gradient(90deg, rgba(3,3,3,0.72) 0%, rgba(3,3,3,0.55) 35%, rgba(3,3,3,0.28) 60%, rgba(3,3,3,0.12) 100%)" }}
      />

      {/* base 40% wash to keep overall contrast consistent */}
      <div className="absolute inset-0 z-[1] pointer-events-none bg-black/25" />

      {/* bottom fade into page background */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 z-[1] pointer-events-none" style={{ background: "linear-gradient(180deg, transparent, #030303)" }} />

      {/* top overline */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 1 }}
        className="absolute top-28 left-6 lg:left-10 z-10 flex items-center gap-3"
      >
        <span className="h-px w-10 bg-[#d4af37]" />
        <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37]">Independent Life Insurance Broker</span>
      </motion.div>

      {/* Main headline — bottom-left asymmetric */}
      <div className="relative z-10 min-h-screen flex flex-col justify-end pb-20 lg:pb-24 px-6 lg:px-14">
        <h1 className="serif font-light leading-[0.92] text-[#f4f0ea] text-[13vw] sm:text-[11vw] lg:text-[6vw] xl:text-[5.6vw] max-w-[92%] lg:max-w-[46%]">
          <Line delay={0.35}>Protection,</Line>
          <Line delay={0.5}>
            <span className="italic text-[#d4af37]">redefined</span> for
          </Line>
          <Line delay={0.65}>the ones you love.</Line>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 1, ease }}
          className="mt-10 flex flex-col lg:flex-row lg:items-end justify-between gap-8"
        >
          <p className="max-w-md text-[#a3a3a3] text-base leading-relaxed tracking-wide">
            Personalized term, whole, and universal life coverage — brokered independently across
            dozens of carriers to secure the very best fit for your family and legacy.
          </p>

          <div className="flex items-center gap-6">
            <a
              href="#quote"
              data-testid="hero-cta"
              className="group inline-flex items-center gap-3 mono text-xs uppercase tracking-[0.2em] px-8 py-4 bg-[#f4f0ea] text-[#030303] rounded-full hover:bg-[#d4af37] transition-colors duration-400"
            >
              Get my quote
              <span className="transition-transform duration-400 group-hover:translate-x-1">→</span>
            </a>
            <a href="#about" data-testid="hero-secondary" className="mono text-xs uppercase tracking-[0.2em] text-[#a3a3a3] link-underline hover:text-[#f4f0ea]">
              Our approach
            </a>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-8 z-10 hidden lg:flex items-center gap-2 text-[#737373]"
      >
        <span className="mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>
    </section>
  );
};
